import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../layout/header";
import { getAllComplaintsURL, getBranchURL } from "../libraries/apiUrl";
import { canViewQueries } from "../libraries/auth";
import { getDefaultAccessibleRoute, getAllowedQueryStatuses } from "../libraries/privileges";
import { _get, _post } from "../libraries/fetch";
const STATUS_FILTERS = [
  { id: "pending", label: "Pending" },
  { id: "inprogress", label: "In-Progress" },
  { id: "solved", label: "Solved" },
  { id: "closed", label: "Closed" }
];

function QueriesPage() {
  const navigate = useNavigate();
  const canAccessQueries = canViewQueries();
  const allowedQueryStatuses = useMemo(() => getAllowedQueryStatuses(), []);
  const visibleStatusFilters = useMemo(
    () => STATUS_FILTERS.filter((filter) => allowedQueryStatuses.includes(filter.id)),
    [allowedQueryStatuses]
  );
  const [queries, setQueries] = useState([]);
  const [branchDetailsMap, setBranchDetailsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("pending");

  useEffect(() => {
    if (!canAccessQueries) {
      navigate(getDefaultAccessibleRoute(), { replace: true });
    }
  }, [canAccessQueries, navigate]);

  useEffect(() => {
    if (visibleStatusFilters.length > 0 && !visibleStatusFilters.some((filter) => filter.id === selectedStatus)) {
      setSelectedStatus(visibleStatusFilters[0].id);
    }
  }, [selectedStatus, visibleStatusFilters]);

  const fetchQueries = useCallback(async () => {
    if (!canAccessQueries) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await _get(getAllComplaintsURL(), { useCache: false });

      if (response?.success === false) {
        throw new Error(response.message || "Failed to load queries.");
      }

      const nextQueries = extractQueryItems(response);
      const nextBranchDetailsMap = await fetchBranchDetailsMap(nextQueries);

      setQueries(nextQueries);
      setBranchDetailsMap(nextBranchDetailsMap);
    } catch (err) {
      setError(err?.message || "Failed to load queries. Please try again.");
      setQueries([]);
      setBranchDetailsMap({});
    } finally {
      setLoading(false);
    }
  }, [canAccessQueries]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const rows = useMemo(
    () => queries
      .map((query) => normalizeQuery(query, branchDetailsMap))
      .filter((query) => matchesQueryStatus(query.status, selectedStatus)),
    [branchDetailsMap, queries, selectedStatus]
  );
  const selectedStatusLabel = visibleStatusFilters.find((filter) => filter.id === selectedStatus)?.label || "Pending";

  if (!canAccessQueries) {
    return null;
  }

  return (
    <div className="main">
      <Header />
      <div style={pageStyle}>
        <div style={headerRowStyle}>
          <div>
            <h1 style={headingStyle}>Queries</h1>
            <div style={statusFilterGroupStyle} aria-label="Query status filter">
              {visibleStatusFilters.map((filter) => (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setSelectedStatus(filter.id)}
                  style={getStatusFilterButtonStyle(selectedStatus === filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <p style={subtitleStyle}>{selectedStatusLabel} Clients Queries</p>
          </div>
          <button type="button" onClick={fetchQueries} disabled={loading} style={refreshButtonStyle}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>

        <div style={tableShellStyle}>
          {loading ? (
            <div style={emptyStyle}>Loading queries...</div>
          ) : error ? (
            <div style={errorStyle}>{error}</div>
          ) : rows.length === 0 ? (
            <div style={emptyStyle}>No {selectedStatusLabel.toLowerCase()} queries found.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={tableHeadRowStyle}>
                    <th style={tableHeaderStyle}>Serial No</th>
                    <th style={tableHeaderStyle}>Client Name</th>
                    <th style={tableHeaderStyle}>Client Package</th>
                    <th style={tableHeaderStyle}>Query</th>
                    <th style={tableHeaderStyle}>Query Date</th>
                    <th style={tableHeaderStyle}>Query Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((query, index) => (
                    <tr
                      key={query.id || `${query.branch}-${query.message}-${index}`}
                      style={{
                        borderBottom: "1px solid #333",
                        backgroundColor: index % 2 === 0 ? "#1a1d23" : "#20232b"
                      }}
                    >
                      <td style={tableCellStyle}>{index + 1}</td>
                      <td style={tableCellStyle}>
                        <button
                          type="button"
                          onClick={() => {
                            if (query.branchId !== "N/A") {
                              navigate(`/branches/${query.branchId}`, {
                                state: {
                                  branchId: query.branchId,
                                  activeRoute: "ClientsQuerries"
                                }
                              });
                            }
                          }}
                          disabled={query.branchId === "N/A"}
                          style={clientNameButtonStyle}
                        >
                          {query.branch !== "N/A" ? query.branch : query.name}
                        </button>
                      </td>
                      <td style={tableCellStyle}>{query.clientPackage}</td>
                      <td style={queryCellStyle}>{query.message}</td>
                      <td style={tableCellStyle}>{formatDate(query.date)}</td>
                      <td style={tableCellStyle}>
                        <span style={statusStyle}>{query.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function extractQueryItems(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const listKeys = [
    "queries",
    "query",
    "complaints",
    "complaint",
    "enquiries",
    "enquiry",
    "inquiries",
    "inquiry",
    "response",
    "data",
    "result",
    "results",
    "records",
    "items",
    "docs"
  ];

  for (const key of listKeys) {
    const value = response[key];

    if (Array.isArray(value)) {
      return value;
    }

    const nestedItems = extractQueryItems(value);
    if (nestedItems.length > 0) {
      return nestedItems;
    }
  }

  return [];
}

async function fetchBranchDetailsMap(queries) {
  const branchIds = [
    ...new Set(
      queries
        .map(getBranchId)
        .filter(Boolean)
        .map(String)
    )
  ];

  if (branchIds.length === 0) {
    return {};
  }

  const branchEntries = await Promise.all(
    branchIds.map(async (branchId) => {
      try {
        const response = await _get(getBranchURL(branchId));
        return [branchId, extractBranchDetails(response)];
      } catch {
        return [branchId, null];
      }
    })
  );

  return branchEntries.reduce((map, [branchId, branch]) => {
    if (branch) {
      map[branchId] = branch;
    }

    return map;
  }, {});
}

function extractBranchDetails(response) {
  if (!response || typeof response !== "object") {
    return null;
  }

  if (response._id || response.id || response.name || response.branchName) {
    return response;
  }

  return response.response || response.data || response.result || null;
}

function normalizeQuery(query, branchDetailsMap) {
  const date = getValue(query, ["createdAt", "created_at", "complaintDate", "queryDate", "date", "submittedAt", "updatedAt"]);
  const name = getValue(query, ["name", "clientName", "fullName", "customerName", "memberName", "username", "complaintBy", "createdBy"]);
  const branchId = getBranchId(query);
  const branchDetails = branchId ? branchDetailsMap[String(branchId)] : null;
  const branch = getBranchValue(query, branchDetails);
  const clientPackage = getPackageValue(query, branchDetails);
  const message = getValue(query, ["message", "complaint", "complaintText", "complaintDescription", "query", "question", "description", "details", "remarks", "comment", "subject"]);
  const status = getValue(query, ["status", "complaintStatus", "queryStatus", "state"]);

  return {
    id: getValue(query, ["_id", "id"]),
    branchId: formatValue(branchId),
    date,
    name: formatValue(name),
    branch: formatValue(branch),
    branchStatus: formatValue(branchDetails?.status),
    clientPackage: formatValue(clientPackage),
    message: formatValue(message),
    status: formatValue(status || "Pending")
  };
}

function matchesQueryStatus(status, selectedStatus) {
  const statusKey = getQueryStatusKey(status);
  const statusAliases = {
    pending: ["pending"],
    inprogress: ["inprogress", "inprocess", "processing", "progress", "ongoing"],
    solved: ["solved", "resolved", "resolve", "completed", "complete", "done"],
    closed: ["closed", "close"]
  };

  return (statusAliases[selectedStatus] || [selectedStatus]).includes(statusKey);
}

function getQueryStatusKey(status) {
  return String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
}

function getBranchId(query) {
  const branchValue = getValue(query, ["branchId", "branch", "branch_id", "gymBranchId", "clientBranchId"]);

  if (branchValue && typeof branchValue === "object") {
    return branchValue._id || branchValue.id || "";
  }

  return branchValue;
}

function getBranchValue(query, branchDetails) {
  if (branchDetails) {
    return branchDetails.name || branchDetails.branchName || branchDetails.gymName || branchDetails._id || branchDetails.id;
  }

  const branch = getValue(query, ["branchName", "gymName", "clientName", "centerName"]);

  if (branch) {
    return branch;
  }

  const branchObject = query?.branch || query?.branchId;
  if (branchObject && typeof branchObject === "object") {
    return branchObject.name || branchObject.branchName || branchObject._id || branchObject.id;
  }

  return branchObject;
}

function getPackageValue(query, branchDetails) {
  const branchPackage = getValue(branchDetails, ["gymPackage", "package", "packageName", "clientPackage", "subscriptionPackage"]);

  if (branchPackage) {
    return branchPackage;
  }

  return getValue(query, ["gymPackage", "package", "packageName", "clientPackage", "subscriptionPackage"]);
}

function getValue(source, keys) {
  if (!source || typeof source !== "object") {
    return "";
  }

  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      return source[key];
    }
  }

  return "";
}

function formatValue(value) {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (Array.isArray(value)) {
    return value.map(formatValue).join(", ");
  }

  if (typeof value === "object") {
    return value.name || value.title || value.label || value._id || value.id || JSON.stringify(value);
  }

  return String(value);
}

function formatDate(dateString) {
  if (!dateString) {
    return "N/A";
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return formatValue(dateString);
  }

  return date.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

const pageStyle = {
  padding: "clamp(16px, 3vw, 20px) clamp(12px, 3vw, 20px)",
  maxWidth: "100%",
  boxSizing: "border-box",
  minHeight: "calc(100vh - 80px)",
  background: "linear-gradient(135deg, #1e2127 0%, #2a2d35 100%)"
};

const headerRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: "20px"
};

const headingStyle = {
  margin: "0 0 8px 0",
  color: "#fff",
  fontSize: "clamp(22px, 5vw, 28px)",
  fontWeight: 700
};

const subtitleStyle = {
  margin: 0,
  color: "#9ca3af",
  fontSize: "15px"
};

const statusFilterGroupStyle = {
  display: "flex",
  gap: "6px",
  flexWrap: "wrap",
  margin: "8px 0"
};

function getStatusFilterButtonStyle(isActive) {
  return {
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 700,
    backgroundColor: isActive ? "#187718" : "#2a2d35",
    color: isActive ? "#fff" : "#d1d5db",
    border: isActive ? "1px solid #187718" : "1px solid #3a3f4b",
    borderRadius: "6px",
    cursor: "pointer"
  };
}

const refreshButtonStyle = {
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: 600,
  backgroundColor: "#187718",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer"
};

const tableShellStyle = {
  backgroundColor: "#1a1d23",
  borderRadius: "12px",
  overflowX: "auto",
  maxWidth: "100%",
  minWidth: 0,
  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
  border: "1px solid #3a3f4b"
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "14px"
};

const tableHeadRowStyle = {
  backgroundColor: "#2a2d35",
  borderBottom: "2px solid #187718"
};

const tableHeaderStyle = {
  padding: "14px 16px",
  textAlign: "left",
  fontWeight: 600,
  color: "#4ade80",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "12px",
  whiteSpace: "nowrap"
};

const tableCellStyle = {
  padding: "0px 12px",
  color: "#fff",
  verticalAlign: "middle"
};

const clientNameButtonStyle = {
  padding: 0,
  border: "none",
  background: "transparent",
  color: "#4ade80",
  font: "inherit",
  fontWeight: 700,
  textAlign: "left",
  cursor: "pointer",
  textDecoration: "underline"
};

const queryCellStyle = {
  ...tableCellStyle,
  minWidth: "240px",
  lineHeight: 1.35
};

const statusStyle = {
  display: "inline-block",
  padding: "3px 8px",
  borderRadius: "4px",
  backgroundColor: "rgba(24, 119, 24, 0.2)",
  color: "#4ade80",
  fontSize: "13px",
  fontWeight: 600
};

const emptyStyle = {
  padding: "40px",
  textAlign: "center",
  color: "#9ca3af"
};

const errorStyle = {
  padding: "18px",
  margin: "20px",
  borderRadius: "8px",
  backgroundColor: "rgba(213, 23, 23, 0.15)",
  border: "1px solid rgba(213, 23, 23, 0.45)",
  color: "#ff9b9b",
  fontWeight: 600
};

export default QueriesPage;
